import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

interface AgencyAnalyticsEmbedProps {
  website: string;
  embedUrl: string;
}

type FrameStatus = "loading" | "loaded" | "error";

// AgencyAnalytics needs scripts, same-origin storage, and ordinary report
// interactions. Deliberately omitted: allow-top-navigation and all device
// permissions. Opening a link in a separate tab remains possible, while the
// report cannot navigate the Core tab itself.
const REPORT_SANDBOX = [
  "allow-downloads",
  "allow-forms",
  "allow-modals",
  "allow-popups",
  "allow-same-origin",
  "allow-scripts",
].join(" ");

const REPORT_LOAD_TIMEOUT_MS = 30_000;

export function AgencyAnalyticsEmbed({
  website,
  embedUrl,
}: AgencyAnalyticsEmbedProps) {
  const [retryCount, setRetryCount] = useState(0);
  const frameKey = `${website}:${embedUrl}:${retryCount}`;
  const [frameState, setFrameState] = useState<{
    key: string;
    status: FrameStatus;
  }>(() => ({ key: frameKey, status: "loading" }));
  // A changed tenant, URL, or retry is loading until that exact frame reports
  // back. Keeping the identity with the state avoids a synchronous reset
  // effect and prevents a late event from the previous frame from becoming
  // current.
  const status = frameState.key === frameKey ? frameState.status : "loading";

  // Cross-origin frame failures are not reported consistently by browsers.
  // Convert a frame that never finishes loading into the same actionable
  // fallback state as an explicit iframe error.
  useEffect(() => {
    if (status !== "loading") return;
    const timeout = window.setTimeout(
      () => setFrameState({ key: frameKey, status: "error" }),
      REPORT_LOAD_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [frameKey, status]);

  return (
    <section aria-labelledby="agency-analytics-report-title" className="min-w-0">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2
            id="agency-analytics-report-title"
            className="text-dash-title font-semibold"
          >
            Performance report
          </h2>
          <p className="mt-1 text-dash-context text-muted-foreground">
            Explore channels, campaigns, and results in the interactive report.
          </p>
        </div>
        <a
          href={embedUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-dash-context font-semibold text-foreground transition-colors hover:border-coral hover:text-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Open report
          <ExternalLink aria-hidden="true" className="size-4" />
        </a>
      </div>

      <div className="relative min-h-[72vh] overflow-hidden rounded-lg border border-border bg-muted/20 shadow-sm lg:min-h-[80vh]">
        <iframe
          key={frameKey}
          title={`${website} performance report`}
          src={embedUrl}
          referrerPolicy="no-referrer"
          sandbox={REPORT_SANDBOX}
          allow=""
          onLoad={() => setFrameState({ key: frameKey, status: "loaded" })}
          onError={() => setFrameState({ key: frameKey, status: "error" })}
          className={`h-[calc(100vh-8rem)] min-h-[72vh] w-full bg-background lg:min-h-[80vh] ${
            status === "error" ? "invisible" : "visible"
          }`}
        />

        {status === "loading" && (
          <div
            role="status"
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background text-center"
          >
            <span
              aria-hidden="true"
              className="size-7 animate-spin rounded-full border-2 border-border border-t-coral"
            />
            <div>
              <div className="text-dash-subtitle font-semibold">Loading your report…</div>
              <div className="mt-1 text-dash-context text-muted-foreground">
                This may take a few seconds.
              </div>
            </div>
          </div>
        )}

        {status === "error" && (
          <div
            role="alert"
            className="absolute inset-0 flex items-center justify-center bg-background px-6 text-center"
          >
            <div className="max-w-md">
              <div className="text-dash-title font-semibold">
                The embedded report couldn't load
              </div>
              <p className="mt-2 text-dash-context text-muted-foreground">
                Stay here and try again, or use Open report above to continue in
                a new tab.
              </p>
              <button
                type="button"
                onClick={() => setRetryCount((count) => count + 1)}
                className="mt-4 rounded-md bg-coral px-4 py-2 text-dash-subtitle font-semibold text-white hover:bg-coral-hover"
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
